/**
 * Global Swiper Component
 * Usage: Wrap any content in <swiper-component> with data attributes for configuration
 *
 * Required structure:
 * <swiper-component data-slides-mobile="1.2" ...>
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">...</div>
 *     </div>
 *     <div class="swiper-pagination"></div>
 *     <div class="swiper-scrollbar"></div>
 *   </div>
 *   <div class="swiper-button-prev"></div>
 *   <div class="swiper-button-next"></div>
 * </swiper-component>
 */

const DESKTOP_BREAKPOINT = 990;

class SwiperComponent extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.initialized = false;
  }

  get isDesktopDisabled() {
    return this.dataset.desktopDisabled === "true";
  }

  get isDesktopViewport() {
    return (
      typeof window !== "undefined" && window.innerWidth >= DESKTOP_BREAKPOINT
    );
  }

  applyDesktopStaticMode() {
    const wrapper = this.querySelector(".swiper-wrapper");
    const columns = parseInt(this.dataset.desktopColumns, 10) || 3;
    if (wrapper) {
      wrapper.style.display = "grid";
      wrapper.style.gap = "var(--swiper-static-gap, 0)";
      wrapper.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
      wrapper.style.overflow = "visible";
    }
    this.classList.add("swiper--desktop-static");
    this.classList.remove("is-initialized");
  }

  clearDesktopStaticMode() {
    const wrapper = this.querySelector(".swiper-wrapper");
    if (wrapper) {
      wrapper.style.display = "";
      wrapper.style.gap = "";
      wrapper.style.gridTemplateColumns = "";
      wrapper.style.overflow = "";
    }
    this.classList.remove("swiper--desktop-static");
  }

  connectedCallback() {
    // Wait for Swiper library to load
    if (typeof Swiper === "undefined") {
      this.waitForSwiper();
    } else {
      this.initSwiper();
    }
  }

  waitForSwiper() {
    const checkSwiper = setInterval(() => {
      if (typeof Swiper !== "undefined") {
        clearInterval(checkSwiper);
        this.initSwiper();
      }
    }, 50);

    // Timeout after 5 seconds
    setTimeout(() => clearInterval(checkSwiper), 5000);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    this._resizeBound = false;
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  handleResize = () => {
    if (this.isDesktopDisabled) {
      if (this.isDesktopViewport) {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
          this.initialized = false;
          this.clearDesktopStaticMode();
          this.applyDesktopStaticMode();
        }
      } else if (this.classList.contains("swiper--desktop-static")) {
        this.clearDesktopStaticMode();
        this.initialized = false;
        this.initSwiper();
      }
    } else if (this.swiper) {
      this.swiper.update();
    }
  };

  initSwiper() {
    if (this.initialized) return;

    const container = this.querySelector(".swiper");
    if (!container) return;

    if (this.dataset.rtl === "true") {
      container.dir = "rtl";
    }

    if (this.isDesktopDisabled && this.isDesktopViewport) {
      this.applyDesktopStaticMode();
      if (!this._resizeBound) {
        window.addEventListener("resize", this.handleResize);
        this._resizeBound = true;
      }
      return;
    }

    const wrapper = container.querySelector(".swiper-wrapper");
    const minSlides = 6;
    if (
      wrapper &&
      this.dataset.loop === "true" &&
      this.dataset.centered === "true"
    ) {
      const slides = Array.from(wrapper.querySelectorAll(".swiper-slide"));
      if (slides.length > 0 && slides.length < minSlides) {
        while (wrapper.querySelectorAll(".swiper-slide").length < minSlides) {
          slides.forEach((slide) => {
            const clone = slide.cloneNode(true);
            clone.setAttribute("aria-hidden", "true");
            wrapper.appendChild(clone);
          });
        }
      }
    }

    const config = this.buildConfig();

    this.swiper = new Swiper(container, config);
    this.initialized = true;
    this.classList.add("is-initialized");

    if (!this._resizeBound) {
      window.addEventListener("resize", this.handleResize);
      this._resizeBound = true;
    }

    // Dispatch event when swiper is ready
    this.dispatchEvent(
      new CustomEvent("swiper:init", {
        detail: { swiper: this.swiper },
        bubbles: true,
      }),
    );
  }

  buildConfig() {
    const getNumber = (value, fallback) => {
      const num = parseFloat(value);
      return isNaN(num) ? fallback : num;
    };

    const spaceMobile = getNumber(this.dataset.spaceMobile, 16);
    const spaceDesktop = getNumber(this.dataset.spaceDesktop, 30);
    const isAuto = this.dataset.slidesView === "auto";

    const config = {
      spaceBetween: spaceMobile,
      grabCursor: true,
      watchOverflow: true,
      watchSlidesProgress: true,
      breakpoints: {},
    };

    if (isAuto) {
      config.slidesPerView = "auto";
      config.breakpoints[990] = {
        slidesPerView: "auto",
        spaceBetween: spaceDesktop,
      };
    } else {
      config.slidesPerView = getNumber(this.dataset.slidesMobile, 1.2);
      config.breakpoints[990] = {
        slidesPerView: getNumber(this.dataset.slidesDesktop, 3),
        spaceBetween: spaceDesktop,
      };
    }

    // Navigation
    const prevBtn = this.querySelector(".swiper-button-prev");
    const nextBtn = this.querySelector(".swiper-button-next");
    if (this.dataset.navigation === "true" && prevBtn && nextBtn) {
      config.navigation = {
        nextEl: nextBtn,
        prevEl: prevBtn,
      };
    }

    // Pagination
    const pagination = this.querySelector(".swiper-pagination");
    if (this.dataset.pagination === "true" && pagination) {
      config.pagination = {
        el: pagination,
        clickable: true,
        dynamicBullets: this.dataset.dynamicBullets === "true",
      };
    }

    // Scrollbar
    const scrollbar = this.querySelector(".swiper-scrollbar");
    if (this.dataset.scrollbar === "true" && scrollbar) {
      config.scrollbar = {
        el: scrollbar,
        draggable: true,
        hide: this.dataset.scrollbarHide === "true",
      };
    }

    // Autoplay
    if (this.dataset.autoplay === "true") {
      config.autoplay = {
        delay: parseInt(this.dataset.autoplayDelay) || 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    // Loop
    if (this.dataset.loop === "true") {
      config.loop = true;
    }

    // Free mode (free scrolling without snapping)
    if (this.dataset.freeMode === "true") {
      config.freeMode = {
        enabled: true,
        sticky: this.dataset.freeSticky === "true",
      };
    }

    // Centered slides (mobile only; desktop uses breakpoint override)
    if (this.dataset.centered === "true") {
      config.centeredSlides = true;
      config.breakpoints[990].centeredSlides = false;
    }

    // Effect
    if (this.dataset.effect) {
      config.effect = this.dataset.effect;

      if (this.dataset.effect === "fade") {
        config.fadeEffect = { crossFade: true };
      }

      if (this.dataset.effect === "coverflow") {
        config.coverflowEffect = {
          rotate: 50,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: false,
        };
      }
    }

    // Speed
    if (this.dataset.speed) {
      config.speed = parseInt(this.dataset.speed);
    }

    // Initial slide
    if (this.dataset.initialSlide) {
      config.initialSlide = parseInt(this.dataset.initialSlide);
    }

    // Direction
    if (this.dataset.direction === "vertical") {
      config.direction = "vertical";
    }

    // Mousewheel
    if (this.dataset.mousewheel === "true") {
      config.mousewheel = {
        forceToAxis: true,
      };
    }

    // Keyboard
    if (this.dataset.keyboard === "true") {
      config.keyboard = {
        enabled: true,
      };
    }

    if (this.dataset.observer === "true") {
      config.observer = true;
      config.observeParents = true;
    }

    return config;
  }

  // Public methods
  slideTo(index, speed) {
    if (this.swiper) {
      this.swiper.slideTo(index, speed);
    }
  }

  slideNext(speed) {
    if (this.swiper) {
      this.swiper.slideNext(speed);
    }
  }

  slidePrev(speed) {
    if (this.swiper) {
      this.swiper.slidePrev(speed);
    }
  }

  update() {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.initialized = false;
      this.classList.remove("is-initialized");
    }
}
}

if (!customElements.get("swiper-component")) {
  customElements.define("swiper-component", SwiperComponent);
}
